import { Container, Sprite, Text, TextStyle } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { HUD, type NoticeKind } from '@/config/hud';
import { makeSprite } from '@/assets/resolver';
import { t } from '@/i18n';
import type { WeaponHolder } from '@/entities/weapons';
import type { CrateDestination } from './shop-types';
import { BatteryGauge } from './hud-battery';
import { BossBar } from './hud-boss';
import { ChargeBars } from './hud-charge';
import { NoticeQueue } from './hud-notices';
import { WeaponStrip, type BagView } from './hud-weapons';
import { UI_FONT } from './widgets';

const ROWS = HUD.rows;

function counter(size: number, fill: number, x: number, y: number): Text {
  const text = new Text({
    text: '0',
    style: new TextStyle({ fontFamily: UI_FONT, fontSize: size, fill, letterSpacing: 1 }),
  });
  text.anchor.set(0, 0.5);
  text.position.set(x, y);
  return text;
}

/**
 * Combat HUD (docs/06): LEFT column — battery, SUPER + DASH bars, gem
 * counters, pilot level, weapon socket strip; it sits at the TV safe-area
 * margin and scales down on phones. TOP BAND — boss bar + name and the
 * queued notice lines under it, kept clear of the left column.
 */
export class Hud {
  readonly view = new Container();
  /** Left column — everything inside is laid out in unscaled px */
  private readonly left = new Container();
  private readonly battery = new BatteryGauge();
  private readonly charge = new ChargeBars();
  private readonly weapons = new WeaponStrip();
  private readonly bossBar = new BossBar();
  private readonly notices = new NoticeQueue();
  private readonly gemCount = counter(22, COLORS.gemYellow, 38, ROWS.gemYellow);
  private readonly gemCountRed = counter(22, COLORS.gemRed, 38, ROWS.gemRed);
  private readonly levelText = counter(16, 0xc9ced6, 4, ROWS.level);
  private readonly sectorText = counter(HUD.sectorTag.size, COLORS.uiAccent, HUD.sectorTag.x, ROWS.level);
  /** Last sector readout (sector, room, total) — repaint only on change */
  private sectorKey = -1;
  private lastYellow = -1;
  private lastRed = -1;
  /** TV safe-area margin (overscan) — recomputed from screen size */
  private margin: number = HUD.baseMargin;
  private hudScale = 1;
  private screenW = 0;
  private screenH = 0;
  /** Top band (boss bar + notices): centre x and usable width, screen px */
  private bandX = 0;
  private bandW = 0;

  constructor() {
    const gemIcon: Sprite = makeSprite('pickup/gem-yellow');
    gemIcon.position.set(16, ROWS.gemYellow);
    const gemIconRed: Sprite = makeSprite('pickup/gem-red');
    gemIconRed.position.set(16, ROWS.gemRed);
    this.levelText.text = 'LV 1';
    this.charge.view.y = ROWS.energy;
    this.weapons.view.y = ROWS.weapons;
    this.left.addChild(
      this.battery.view, this.charge.view,
      gemIcon, this.gemCount, gemIconRed, this.gemCountRed, this.levelText, this.sectorText,
      this.weapons.view,
    );
    this.view.addChild(this.left, this.bossBar.view, this.notices.view);
  }

  /** TV safe-area margin + small-screen scale for the left column (docs/06) */
  layout(screenW: number, screenH: number): void {
    if (screenW === this.screenW && screenH === this.screenH) return;
    this.screenW = screenW;
    this.screenH = screenH;
    this.margin = Math.max(HUD.baseMargin, Math.round(Math.min(screenW, screenH) * HUD.safeAreaPct));
    this.hudScale = Math.max(
      HUD.minScale, Math.min(1, screenW / HUD.refW, screenH / HUD.refH),
    );
    this.left.position.set(this.margin, this.margin);
    this.left.scale.set(this.hudScale);
    this.layoutTopBand();
  }

  /** Keep the boss bar + notices clear of the left column and pause button. */
  private layoutTopBand(): void {
    const tb = HUD.topBand;
    const extent = Math.max(HUD.leftColumnW, this.battery.rowRight) * this.hudScale;
    const x0 = this.margin + extent + tb.gap;
    const x1 = this.screenW - this.margin - tb.rightReserve;
    const half = Math.min(this.screenW / 2 - x0, x1 - this.screenW / 2);
    if (half * 2 >= tb.minCenteredW) {
      this.bandX = this.screenW / 2;
      this.bandW = half * 2;
    } else {
      // Phones: centre in the free region right of the left column
      this.bandX = (x0 + x1) / 2;
      this.bandW = Math.max(0, x1 - x0);
    }
    this.notices.layout(
      this.bandX, this.margin + HUD.notices.bandOffset, this.bandW, this.hudScale,
    );
  }

  setLevel(level: number): void {
    const label = `LV ${level}`;
    if (this.levelText.text !== label) this.levelText.text = label;
  }

  /** Sector run position (docs/01); sector 0 = endless (hidden). */
  setSector(sector: number, room: number, total: number): void {
    const key = sector * 10000 + room * 100 + total;
    if (key === this.sectorKey) return;
    this.sectorKey = key;
    this.sectorText.text = sector === 0 ? ''
      : room >= total ? t('hud.sectorFinale', { n: sector })
      : t('hud.sectorRoom', { n: sector, room, total });
  }

  setHp(hp: number, maxHp: number, armor: number): void {
    // A wider battery (hull cards / armor) pushes the top band right
    if (this.battery.set(hp, maxHp, armor) && this.screenW > 0) this.layoutTopBand();
  }

  setGems(yellow: number, red: number): void {
    if (yellow === this.lastYellow && red === this.lastRed) return;
    this.lastYellow = yellow;
    this.lastRed = red;
    this.gemCount.text = String(yellow);
    this.gemCountRed.text = String(red);
  }

  /** SUPER energy 0..1 + dash recharge 0..1 (1 = ready). */
  setEnergy(pct: number, dashReadyPct = 1): void {
    this.charge.set(pct, dashReadyPct);
  }

  /** Socket strip + bag chip — every frame; repaints only on changes. */
  setWeapons(holder: WeaponHolder, bag: BagView): void {
    this.weapons.sync(holder, bag);
  }

  /** Wordless pickup toast: weapon glyph → socket / bag / swap / gems. */
  weaponPickup(weaponId: string, dest: CrateDestination, socket: number): void {
    this.weapons.showPickup(weaponId, dest, socket);
  }

  /** Boss bar: null name hides it */
  setBoss(name: string | null, pct = 0, screenW = 0): void {
    this.bossBar.set(name, pct, screenW, this.bandX, this.bandW, this.margin);
  }

  /** Queue a message in the top band (priority from its kind). */
  notify(text: string, kind: NoticeKind, color?: number): void {
    this.notices.push(text, kind, color);
  }

  /** Armor pickup feedback: short scale pulse on the battery block. */
  flashArmor(): void {
    this.battery.flashArmor();
  }

  update(dt: number): void {
    this.battery.update(dt);
    this.charge.update(dt);
    this.notices.update(dt);
    this.weapons.update(dt);
  }
}
